import { loadComptesTva } from '@application/comptabilite/ComptesTvaStore';
import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Facture } from '@shared/types/Facture';
import { fmtFCFA, fmtDate, fmtPeriode, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { canMutateComptaRecords } from '@application/access/ViewAccessPolicy';
import { onExclusive } from '@presentation/shell/viewDom';
import { openFormModal } from '@presentation/shell/openFormModal';
import { renderRowActions } from '@presentation/shell/rowActions';
import { FactureClientFormView } from '@presentation/views/forms/FactureClientFormView';
import { FactureFournisseurFormView } from '@presentation/views/forms/FactureFournisseurFormView';
import { previewDocument } from '@presentation/print/PdfService';
import { buildFactureHTML } from '@presentation/print/factureTemplate';
import { downloadCsv, downloadBlob } from '@presentation/shell/download';
import { ETVAExporter } from '@domain/fiscalite/eTVAExporter';
import { DeclarationTVAService } from '@domain/fiscalite/DeclarationTVAService';
import type { DeclarationTVA } from '@domain/fiscalite/DeclarationTVAService';
import { uuid } from '@shared/utils/uuid';
import type { ConditionsReglement } from '@shared/types/Tiers';
import type { Reglement } from '@shared/types/Reglement';
import {
  compteTresoPourMode,
  genererEcrituresReglement,
  resteAPayer,
  statutApresReglements,
  validerReglement,
} from '@domain/comptabilite/ReglementService';
import { runAtomicWrites } from '@application/persistence/AtomicBatch';
import { FormHelper } from '@presentation/components/FormHelper';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
} from '@presentation/shell/localCrud';

const VENTE_TYPES = new Set(['vente', 'standard', 'acompte', 'avoir']);

function kpi(label: string, value: string): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

async function loadFactures(ctx: Parameters<ViewController>[1]): Promise<Facture[]> {
  return ctx.factures.list();
}

function statutPill(statut: string): string {
  const ok = /pay/i.test(statut);
  const warn = /brouillon|emise|comptabilis/i.test(statut);
  const cls = ok ? 'success' : warn ? 'warning' : 'info';
  return `<span class="pill ${cls}">${esc(statut)}</span>`;
}

function facturesTable(
  section: HTMLElement,
  tableId: string,
  list: Facture[],
  tiersLabel: string,
): void {
  const tbody = section.querySelector(`#${tableId} tbody`);
  if (!tbody) return;
  tbody.innerHTML =
    list.length === 0
      ? `<tr><td colspan="8" style="color:var(--gris);padding:14px;">Aucune facture.</td></tr>`
      : list
          .sort((a, b) => b.date.localeCompare(a.date))
          .map((f) => {
            const annulable =
              f.statut !== 'annulee' && f.statut !== 'annule' && !/pay/i.test(f.statut);
            const payable =
              f.statut !== 'annulee' && f.statut !== 'annule' && f.statut !== 'payee' && f.statut !== 'paye';
            const actions = renderRowActions(f.id, [
              { action: 'view', label: 'Voir' },
              { action: 'print', label: 'PDF' },
              ...(payable ? [{ action: 'pay', label: 'Règlement' }] : []),
              ...(annulable
                ? [{ action: 'cancel', label: 'Annuler', variant: 'danger' as const }]
                : []),
            ]);
            return `<tr>
        <td class="mono">${esc(f.numero)}</td>
        <td>${esc(fmtDate(f.date))}</td>
        <td>${esc(f.objet || f.client_id || tiersLabel)}</td>
        <td class="num">${esc(fmtFCFA(f.total_ht))}</td>
        <td class="num">${esc(fmtFCFA(f.total_tva))}</td>
        <td class="num">${esc(fmtFCFA(f.total_ttc))}</td>
        <td>${statutPill(f.statut)}</td>
        <td class="num">${actions}</td>
      </tr>`;
          })
          .join('');
}

function fillKpis(section: HTMLElement, gridId: string, list: Facture[]): void {
  const grid = section.querySelector<HTMLElement>(`#${gridId}`);
  if (!grid) return;
  const ht = list.reduce((s, f) => s + (f.total_ht || 0), 0);
  const tva = list.reduce((s, f) => s + (f.total_tva || 0), 0);
  const ttc = list.reduce((s, f) => s + (f.total_ttc || 0), 0);
  grid.innerHTML =
    kpi('Factures', String(list.length)) +
    kpi('Total HT', fmtFCFA(ht)) +
    kpi('TVA', fmtFCFA(tva)) +
    kpi('Total TTC', fmtFCFA(ttc));
}

function bindFactureActions(
  section: HTMLElement,
  tableId: string,
  list: Facture[],
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
): void {
  const tbody = section.querySelector(`#${tableId} tbody`);
  onExclusive(tbody, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn) return;
    const f = list.find((x) => x.id === btn.dataset.id);
    if (!f) return;
    if (btn.dataset.action === 'view') {
      ToastService.info(
        `${f.numero} — ${fmtFCFA(f.total_ttc)} — échéance ${fmtDate(f.date_echeance)}`,
      );
      return;
    }
    if (btn.dataset.action === 'print') {
      const tenant = ctx.tenantContext.tryGetTenant();
      void (async () => {
        const tid = tenantIdOf(ctx);
        const declared = await loadRegister<TiersLocal>(tid, 'tiers');
        const typeWanted: 'client' | 'fournisseur' = f.type === 'achat' ? 'fournisseur' : 'client';
        const tiers =
          declared.find((t) => t.code === f.client_id && (t.type === typeWanted || t.type === 'mixte')) ||
          declared.find((t) => t.code === f.client_id || t.id === f.client_id) ||
          null;
        previewDocument(
          buildFactureHTML(f, tenant, tiers),
          `Facture_${f.numero}.pdf`.replace(/\s+/g, '_'),
          `Facture ${f.numero}`,
        );
      })();
      return;
    }
    if (btn.dataset.action === 'pay') {
      if (!canMutateComptaRecords(shell.permission)) {
        ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
        return;
      }
      void enregistrerReglementFacture(section, ctx, shell, f);
      return;
    }
    if (btn.dataset.action === 'cancel') {
      if (!canMutateComptaRecords(shell.permission)) {
        ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
        return;
      }
      void (async () => {
        const ok = await confirmAction({
          host: section,
          title: 'Annuler la facture',
          message: `La facture ${f.numero} sera annulée et ses écritures (dont TVA) seront extournées.`,
          confirmLabel: 'Annuler la facture',
          danger: true,
        });
        if (!ok) return;
        const journal = await ctx.ecritures.list();
        const aExtourner = journal.filter(
          (e) =>
            e.source !== 'extourne' &&
            (e.facture_id === f.id || e.numero_piece === f.numero),
        );
        const dejaExtournees = new Set(
          journal.filter((e) => e.source === 'extourne' && e.facture_id === f.id).map((e) => e.groupe_id),
        );
        const extournes = aExtourner
          .filter((e) => !dejaExtournees.has(e.id) && !dejaExtournees.has(e.groupe_id))
          .map((e) => ({
            ...e,
            id: uuid('ecr'),
            debit: e.credit,
            credit: e.debit,
            libelle: `Extourne — ${e.libelle}`,
            source: 'extourne' as const,
            groupe_id: e.id,
            genere_le: new Date().toISOString(),
          }));
        try {
          await runAtomicWrites([
            {
              table: 'factures',
              id: f.id,
              run: async () => {
                await ctx.factures.update({ ...f, statut: 'annulee' });
              },
              rollback: async () => {
                await ctx.factures.update(f);
              },
            },
            ...extournes.map((e) => ({
              table: 'ecritures',
              id: e.id,
              run: async () => {
                await ctx.ecritures.add(e);
              },
              rollback: async () => {
                await ctx.ecritures.delete(e.id);
              },
            })),
          ]);
        } catch (err) {
          ToastService.error(`Annulation non enregistrée (rollback) : ${String(err)}`);
          return;
        }
        ToastService.success(`Facture ${f.numero} annulée`);
        void shell.reload();
      })();
    }
  });
}

async function enregistrerReglementFacture(
  section: HTMLElement,
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
  facture: Facture,
): Promise<void> {
  const tid = tenantIdOf(ctx);
  const existants = await loadRegister<Reglement>(tid, 'reglements');
  const reste = resteAPayer(facture, existants);
  if (reste <= 0) {
    ToastService.info(`La facture ${facture.numero} est déjà soldée`);
    return;
  }
  openFormModal(section, { title: `Règlement ${facture.numero}`, size: 'sm' }, (host, close) => {
    host.innerHTML = `
      <form id="reglement-form">
        ${FormHelper.field('date', 'Date', 'date', { required: true, value: new Date().toISOString().slice(0, 10) })}
        ${FormHelper.field('montant', 'Montant (FCFA)', 'number', { required: true, value: String(reste) })}
        ${FormHelper.select(
          'mode',
          'Mode',
          [
            { value: 'virement', label: 'Virement' },
            { value: 'cheque', label: 'Chèque' },
            { value: 'especes', label: 'Espèces' },
            { value: 'mobile_money', label: 'Mobile money' },
          ],
          { value: 'virement', required: true },
        )}
        ${FormHelper.select(
          'compte_tresorerie',
          'Compte de trésorerie',
          [
            { value: '521', label: '521 — Banques locales' },
            { value: '571', label: '571 — Caisse' },
          ],
          { value: '521', required: true },
        )}
        <div class="form-actions"><button type="submit" class="btn btn-primary">Enregistrer</button></div>
      </form>`;
    const formEl = host.querySelector<HTMLFormElement>('#reglement-form');
    const modeSelect = formEl?.querySelector<HTMLSelectElement>('[name="mode"]');
    const compteSelect = formEl?.querySelector<HTMLSelectElement>('[name="compte_tresorerie"]');
    const syncCouple = (): void => {
      if (!modeSelect || !compteSelect) return;
      compteSelect.value = compteTresoPourMode(modeSelect.value as Reglement['mode']);
    };
    modeSelect?.addEventListener('change', syncCouple);
    formEl?.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void (async () => {
        const form = ev.target as HTMLFormElement;
        const data = new FormData(form);
        const montant = Number(data.get('montant'));
        const mode = (String(data.get('mode')) as Reglement['mode']) || 'virement';
        try {
          validerReglement(facture, existants, montant);
        } catch (err) {
          ToastService.error(err instanceof Error ? err.message : `Montant invalide (reste dû : ${reste} FCFA)`);
          return;
        }
        const reglement: Reglement = {
          id: uuid('rgl'),
          tenant_id: facture.tenant_id,
          facture_id: facture.id,
          date: String(data.get('date') || new Date().toISOString().slice(0, 10)),
          montant,
          mode,
          compte_tresorerie: compteTresoPourMode(mode),
          cree_le: new Date().toISOString(),
        };
        const compteTiers = facture.type === 'achat' ? '401' : '411';
        const ecritures = genererEcrituresReglement(facture, reglement, compteTiers);
        const suivants = [...existants, reglement];
        const statutAvant = facture.statut;
        const statutApres = statutApresReglements(facture, suivants);
        try {
          await runAtomicWrites([
            {
              table: 'reglements',
              id: reglement.id,
              run: async () => {
                await saveRegister(tid, 'reglements', suivants);
              },
              rollback: async () => {
                await saveRegister(tid, 'reglements', existants);
              },
            },
            ...ecritures.map((e) => ({
              table: 'ecritures',
              id: e.id,
              run: async () => {
                await ctx.ecritures.add(e);
              },
              rollback: async () => {
                await ctx.ecritures.delete(e.id);
              },
            })),
            {
              table: 'factures',
              id: facture.id,
              run: async () => {
                await ctx.factures.update({ ...facture, statut: statutApres });
              },
              rollback: async () => {
                await ctx.factures.update({ ...facture, statut: statutAvant });
              },
            },
          ]);
        } catch (err) {
          ToastService.error(`Règlement non enregistré (rollback) : ${String(err)}`);
          return;
        }
        ToastService.success(`Règlement de ${fmtFCFA(montant)} enregistré`);
        close();
        void shell.reload();
      })();
    });
  });
}

function wireFactureModal(
  section: HTMLElement,
  shell: Parameters<ViewController>[2],
  view: 'ventes' | 'achats',
): void {
  const w = window as unknown as Record<string, unknown>;
  w['openFactureModal'] = (type?: string) => {
    if (!canMutateComptaRecords(shell.permission)) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const achat = type === 'achat' || view === 'achats';
    openFormModal(
      section,
      { title: achat ? 'Nouvelle facture achat' : 'Nouvelle facture vente', size: 'lg' },
      (host, close) => {
        const onSaved = (): void => {
          close();
          void shell.reload();
        };
        if (achat) FactureFournisseurFormView.render(host, onSaved, close);
        else FactureClientFormView.render(host, onSaved, close);
      },
    );
  };
}

export const ventesController: ViewController = async (section, ctx, shell) => {
  const factures = (await loadFactures(ctx)).filter((f) => VENTE_TYPES.has(f.type));
  fillKpis(section, 'ventes-kpi-grid', factures);
  facturesTable(section, 'ventes-table', factures, 'Client');
  bindFactureActions(section, 'ventes-table', factures, ctx, shell);
  wireFactureModal(section, shell, 'ventes');
};

export const achatsController: ViewController = async (section, ctx, shell) => {
  const factures = (await loadFactures(ctx)).filter((f) => f.type === 'achat');
  fillKpis(section, 'achats-kpi-grid', factures);
  facturesTable(section, 'achats-table', factures, 'Fournisseur');
  bindFactureActions(section, 'achats-table', factures, ctx, shell);
  wireFactureModal(section, shell, 'achats');
};

export const tvaController: ViewController = async (section, ctx) => {
  const [factures, ecritures] = await Promise.all([loadFactures(ctx), ctx.ecritures.list()]);
  const tid = tenantIdOf(ctx);
  const credits = await loadRegister<{ periode: string; credit: number }>(tid, 'tva_credits');
  const periodes = Array.from(
    new Set([
      ...factures.map((f) => (f.date || '').slice(0, 7)),
      ...ecritures.map((e) => e.periode),
    ]),
  )
    .filter(Boolean)
    .sort();
  let current = periodes[periodes.length - 1] ?? new Date().toISOString().slice(0, 7);

  const picker = section.querySelector<HTMLInputElement>('#tva-month-picker');
  const label = section.querySelector('#tva-periode-label');

  const comptesTva = await loadComptesTva(tid);
  const creditAnterieur = (periode: string): number =>
    DeclarationTVAService.creditAnterieur(ecritures, periode, credits, comptesTva);

  const declarationDuMois = (): DeclarationTVA =>
    DeclarationTVAService.generer(ecritures, current, creditAnterieur(current), comptesTva);

  const persistCredit = (periode: string, credit: number): void => {
    const next = credits.filter((c) => c.periode !== periode);
    next.push({ periode, credit });
    credits.length = 0;
    credits.push(...next);
    void saveRegister(tid, 'tva_credits', next);
  };

  const actives = (list: Facture[]): Facture[] =>
    list.filter((f) => f.statut !== 'annulee' && f.statut !== 'annule');

  const render = (): void => {
    if (picker) picker.value = current;
    if (label) label.textContent = fmtPeriode(current);
    const decl = declarationDuMois();
    persistCredit(current, decl.credit_reportable);
    const mois = actives(factures.filter((f) => (f.date || '').slice(0, 7) === current));
    const ventes = mois.filter((f) => VENTE_TYPES.has(f.type));
    const achats = mois.filter((f) => f.type === 'achat');
    const deductible = decl.tva_deductible_biens + decl.tva_deductible_immo;

    const grid = section.querySelector<HTMLElement>('#tva-kpi-grid');
    if (grid)
      grid.innerHTML =
        kpi('TVA collectée', fmtFCFA(decl.tva_collectee)) +
        kpi('TVA déductible', fmtFCFA(deductible)) +
        kpi(decl.tva_due > 0 ? 'TVA à payer' : 'Crédit reportable', fmtFCFA(decl.tva_due || decl.credit_reportable));

    const coll = section.querySelector('#tva-coll-table tbody');
    if (coll) {
      coll.innerHTML =
        ventes.length === 0
          ? '<tr><td colspan="5" style="color:var(--gris);padding:12px;">Aucune vente.</td></tr>'
          : ventes
              .map(
                (f) =>
                  `<tr><td>${esc(fmtDate(f.date))}</td><td class="mono">${esc(f.numero)}</td><td>${esc(f.client_id || '—')}</td><td class="num">${esc(fmtFCFA(f.total_ht))}</td><td class="num">${esc(fmtFCFA(f.total_tva))}</td></tr>`,
              )
              .join('');
    }
    const ded = section.querySelector('#tva-ded-table tbody');
    if (ded) {
      ded.innerHTML =
        achats.length === 0
          ? '<tr><td colspan="5" style="color:var(--gris);padding:12px;">Aucun achat.</td></tr>'
          : achats
              .map(
                (f) =>
                  `<tr><td>${esc(fmtDate(f.date))}</td><td class="mono">${esc(f.numero)}</td><td>${esc(f.client_id || '—')}</td><td class="num">${esc(fmtFCFA(f.total_ht))}</td><td class="num">${esc(fmtFCFA(f.total_tva))}</td></tr>`,
              )
              .join('');
    }
    const liq = section.querySelector('#tva-liquid-tbody');
    if (liq) {
      liq.innerHTML =
        `<tr><td>TVA collectée (442 / 4431)</td><td class="num">${esc(fmtFCFA(decl.tva_collectee))}</td></tr>` +
        `<tr><td>TVA déductible biens (444 / 4452)</td><td class="num">- ${esc(fmtFCFA(decl.tva_deductible_biens))}</td></tr>` +
        `<tr><td>TVA déductible immobilisations (4451)</td><td class="num">- ${esc(fmtFCFA(decl.tva_deductible_immo))}</td></tr>` +
        `<tr><td>Crédit antérieur reporté</td><td class="num">- ${esc(fmtFCFA(decl.credit_anterieur))}</td></tr>` +
        `<tr class="total"><td><strong>${decl.tva_due > 0 ? 'TVA nette à payer' : 'Crédit de TVA reportable'}</strong></td><td class="num"><strong>${esc(fmtFCFA(decl.tva_due || decl.credit_reportable))}</strong></td></tr>`;
    }
  };
  render();

  const w = window as unknown as Record<string, unknown>;
  w['changePeriode'] = (delta: number) => {
    const [y, m] = current.split('-').map(Number);
    const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
    current = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    render();
  };
  w['setPeriode'] = (value: string) => {
    if (value) {
      current = value;
      render();
    }
  };
  w['exportDeclarationTVA'] = () => {
    const decl = declarationDuMois();
    const header = ['Poste', 'Compte', 'Montant'];
    const lines = [
      ['collectee', '4431', decl.tva_collectee].join(';'),
      ['deductible_biens', '4452', decl.tva_deductible_biens].join(';'),
      ['deductible_immo', '4451', decl.tva_deductible_immo].join(';'),
      ['credit_anterieur', '', decl.credit_anterieur].join(';'),
      ['tva_due', '', decl.tva_due].join(';'),
      ['credit_reportable', '', decl.credit_reportable].join(';'),
    ];
    downloadCsv([header.join(';'), ...lines].join('\n'), `tva-${current}.csv`);
    ToastService.success(`Déclaration TVA ${current} exportée`);
  };
  w['exportEtvaXml'] = () => {
    const declaration = declarationDuMois();
    const tenant = ctx.tenantContext.tryGetTenant();
    const xml = ETVAExporter.genererXML(
      declaration,
      tenant?.nif || '',
      tenant?.raison_sociale || tenant?.nom || 'Entreprise',
    );
    downloadBlob(new Blob([xml], { type: 'application/xml;charset=utf-8' }), `etva-${current}.xml`);
    ToastService.success(`Fichier e-TVA ${current} généré`);
  };
};

export interface TiersLocal {
  id: string;
  code: string;
  type: 'client' | 'fournisseur' | 'mixte';
  raison_sociale: string;
  nif: string;
  rccm: string;
  adresse: string;
  ville: string;
  telephone: string;
  email: string;
  contact_principal: string;
  iban: string;
  compte_bancaire: string;
  conditions_reglement: ConditionsReglement;
}

function emptyTiersFields(): Omit<TiersLocal, 'id' | 'code' | 'type' | 'raison_sociale'> {
  return {
    nif: '',
    rccm: '',
    adresse: '',
    ville: '',
    telephone: '',
    email: '',
    contact_principal: '',
    iban: '',
    compte_bancaire: '',
    conditions_reglement: '30j',
  };
}

export function mergeTiersWithFactures(
  declared: TiersLocal[],
  factures: Facture[],
  reglements: readonly Reglement[],
): Array<TiersLocal & { solde: number }> {
  const soldes = new Map<string, number>();
  const derived = new Map<string, TiersLocal>();
  for (const f of factures) {
    if (f.statut === 'annulee' || f.statut === 'annule') continue;
    const type: 'client' | 'fournisseur' = f.type === 'achat' ? 'fournisseur' : 'client';
    const code = f.client_id || '—';
    const key = `${type}:${code}`;
    soldes.set(key, (soldes.get(key) ?? 0) + resteAPayer(f, reglements));
    if (!derived.has(key)) {
      derived.set(key, {
        id: `derived-${type}-${code}`,
        code,
        type,
        raison_sociale: code,
        ...emptyTiersFields(),
      });
    }
  }
  const byKey = new Map<string, TiersLocal>();
  for (const t of derived.values()) byKey.set(`${t.type}:${t.code}`, t);
  for (const t of declared) byKey.set(`${t.type}:${t.code}`, t);
  return [...byKey.values()].map((t) => {
    if (t.type === 'mixte') {
      return {
        ...t,
        solde: (soldes.get(`client:${t.code}`) ?? 0) + (soldes.get(`fournisseur:${t.code}`) ?? 0),
      };
    }
    return { ...t, solde: soldes.get(`${t.type}:${t.code}`) ?? 0 };
  });
}

export const tiersController: ViewController = async (section, ctx, shell) => {
  const factures = await loadFactures(ctx);
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateComptaRecords(shell.permission);
  let declared = await loadRegister<TiersLocal>(tid, 'tiers');
  const reglements = await loadRegister<Reglement>(tid, 'reglements');

  const render = (): void => {
    const list = mergeTiersWithFactures(declared, factures, reglements);
    const grid = section.querySelector<HTMLElement>('#tiers-kpi-grid');
    if (grid) {
      const clients = list.filter((t) => t.type === 'client' || t.type === 'mixte').length;
      const fournisseurs = list.filter(
        (t) => t.type === 'fournisseur' || t.type === 'mixte',
      ).length;
      grid.innerHTML =
        kpi('Tiers', String(list.length)) +
        kpi('Clients', String(clients)) +
        kpi('Fournisseurs', String(fournisseurs));
    }
    const w = window as unknown as Record<string, unknown>;
    const filter = (w['_tiersFilter'] as string) || '';
    const rows = list.filter((t) => !filter || t.type === filter);
    const tbody = section.querySelector('#tiers-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      rows.length === 0
        ? emptyRow(7, 'Aucun tiers enregistré.')
        : rows
            .map((t) => {
              const persisted = !t.id.startsWith('derived-');
              const typeLabel =
                t.type === 'client' ? 'Client' : t.type === 'fournisseur' ? 'Fournisseur' : 'Mixte';
              const pillCls =
                t.type === 'client' ? 'info' : t.type === 'fournisseur' ? 'warning' : 'success';
              return `<tr>
          <td class="mono">${esc(t.code)}</td>
          <td><span class="pill ${pillCls}">${typeLabel}</span></td>
          <td>${esc(t.raison_sociale)}</td>
          <td>${esc(t.nif || '—')}<br><small style="color:var(--gris);">${esc(t.rccm || 'RCCM —')}</small></td>
          <td>${esc(t.telephone || '—')}</td>
          <td class="num">${esc(fmtFCFA(t.solde))}</td>
          <td class="num">${persisted ? crudActions(t.id) : '<span class="pill">Facture</span>'}</td>
        </tr>`;
            })
            .join('');
  };
  render();

  const openEditor = (existing?: TiersLocal): void => {
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier le tiers' : 'Nouveau tiers',
      size: 'lg',
      fields: [
        {
          kind: 'select',
          name: 'type',
          label: 'Type',
          options: [
            { value: 'client', label: 'Client' },
            { value: 'fournisseur', label: 'Fournisseur' },
            { value: 'mixte', label: 'Client & fournisseur (mixte)' },
          ],
        },
        { kind: 'text', name: 'code', label: 'Code', required: true, placeholder: 'CLI-001' },
        { kind: 'text', name: 'raison_sociale', label: 'Raison sociale', required: true },
        {
          kind: 'text',
          name: 'nif',
          label: 'NIF',
          required: false,
          placeholder: 'N° d’identification fiscale',
        },
        {
          kind: 'text',
          name: 'rccm',
          label: 'RCCM',
          required: false,
          placeholder: 'N° RCCM',
        },
        { kind: 'text', name: 'adresse', label: 'Adresse', required: false },
        { kind: 'text', name: 'ville', label: 'Ville', required: false },
        { kind: 'text', name: 'telephone', label: 'Téléphone', required: false },
        { kind: 'email', name: 'email', label: 'E-mail', required: false },
        { kind: 'text', name: 'contact_principal', label: 'Contact principal', required: false },
        { kind: 'text', name: 'iban', label: 'IBAN', required: false, placeholder: 'GA21…' },
        {
          kind: 'text',
          name: 'compte_bancaire',
          label: 'Compte bancaire',
          required: false,
          placeholder: 'N° de compte',
        },
        {
          kind: 'select',
          name: 'conditions_reglement',
          label: 'Conditions de règlement',
          options: [
            { value: 'comptant', label: 'Comptant' },
            { value: '30j', label: '30 jours' },
            { value: '60j', label: '60 jours' },
            { value: '90j', label: '90 jours' },
          ],
        },
      ],
      values: existing ? { ...emptyTiersFields(), ...existing } : undefined,
      onSubmit: async (data) => {
        const typeRaw = String(data['type'] ?? 'client');
        const type: TiersLocal['type'] =
          typeRaw === 'fournisseur' ? 'fournisseur' : typeRaw === 'mixte' ? 'mixte' : 'client';
        const condRaw = String(data['conditions_reglement'] ?? '30j');
        const conditions: ConditionsReglement =
          condRaw === 'comptant' || condRaw === '60j' || condRaw === '90j' ? condRaw : '30j';
        const row: TiersLocal = {
          id: existing?.id.startsWith('derived-') ? uuid('trs') : (existing?.id ?? uuid('trs')),
          code: String(data['code']),
          type,
          raison_sociale: String(data['raison_sociale']),
          nif: String(data['nif'] ?? ''),
          rccm: String(data['rccm'] ?? ''),
          adresse: String(data['adresse'] ?? ''),
          ville: String(data['ville'] ?? ''),
          telephone: String(data['telephone'] ?? ''),
          email: String(data['email'] ?? ''),
          contact_principal: String(data['contact_principal'] ?? ''),
          iban: String(data['iban'] ?? ''),
          compte_bancaire: String(data['compte_bancaire'] ?? ''),
          conditions_reglement: conditions,
        };
        declared = upsertById(declared, row);
        await saveRegister(tid, 'tiers', declared);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#tiers-table tbody'),
    section,
    (id) => openEditor(declared.find((x) => x.id === id)),
    async (id) => {
      declared = removeById(declared, id);
      await saveRegister(tid, 'tiers', declared);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['renderTiers'] = () => render();
  // Onglets Clients / Fournisseurs / Tous : bascule l'onglet, fixe le filtre puis redessine (ancien code en ligne).
  w['setTiersTab'] = (btn: HTMLElement, tabId: string, filter: string) => {
    (w['switchTab'] as (b: HTMLElement, id: string) => void)(btn, tabId);
    w['_tiersFilter'] = filter;
    render();
  };
  w['openTiersModal'] = () => openEditor();
};
